<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { NButton, NCard, NBadge, NTag } from 'naive-ui'
import {
    LANGUAGES, getCurrentLanguage, setCurrentLanguage,
    getLocaleFromPreferred
} from '@/services/language'
import { dt } from '@/utils/i18n'
import clientDashboardImage from '@/assets/images/dulcey-lima-46rGW30npHQ-unsplash.jpg'
import encryptionImage from '@/assets/images/markus-winkler-wZsE5PzozIc-unsplash.jpg'
import documentImage from '@/assets/images/bernd-dittrich-JK7o8r8ruzs-unsplash.jpg'
import sharingImage from '@/assets/images/gabrielle-henderson-HJckKnwCXxQ-unsplash.jpg'

const router = useRouter()
const { t, locale } = useI18n()

onMounted(() => {
    locale.value = getLocaleFromPreferred(getCurrentLanguage())
})

const handleClientRegister = () => {
    router.push({
        name: 'register',
        query: { role: 'client' }
    })
}

const handleClientLogin = () => {
    router.push({
        name: 'login',
        query: { role: 'client' }
    })
}
</script>

<template>
    <div class="client-page">
        <!-- Hero Section for Clients -->
        <section class="client-hero">
            <div class="container">
                <div class="hero-grid">
                    <div class="hero-content">
                        <h1 class="hero-title">{{ dt('clientsHome.heroTitle') }}</h1>
                        <p class="hero-subtitle">{{ dt('clientsHome.heroSubtitle') }}</p>

                        <div class="cta-buttons">
                            <NButton type="primary" size="large" class="cta-button-primary"
                                @click="handleClientRegister">
                                {{ dt('clientsHome.getStarted') }}
                            </NButton>
                            <NButton size="large" class="cta-button-secondary" @click="handleClientLogin">
                                {{ dt('clientsHome.clientLogin') }}
                            </NButton>
                        </div>

                        <div class="hero-stats">
                            <div class="stat-item">
                                <div class="stat-number">100%</div>
                                <div class="stat-label">{{ dt('clientsHome.stats.secure') }}</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-number">24/7</div>
                                <div class="stat-label">{{ dt('clientsHome.stats.access') }}</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-number">12</div>
                                <div class="stat-label">{{ dt('clientsHome.stats.languages') }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="hero-image">
                        <img :src="clientDashboardImage" alt="Client Dashboard" class="dashboard-preview" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Security First Section -->
        <section class="security-first">
            <div class="container">
                <h2 class="section-title">{{ dt('clientsHome.securityTitle') }}</h2>
                <div class="security-grid">
                    <div class="security-card">
                        <div class="security-icon">🔐</div>
                        <h3>{{ dt('clientsHome.security.confidential.title') }}</h3>
                        <p>{{ dt('clientsHome.security.confidential.description') }}</p>
                    </div>
                    <div class="security-card">
                        <div class="security-icon">🔒</div>
                        <h3>{{ dt('clientsHome.security.encryption.title') }}</h3>
                        <p>{{ dt('clientsHome.security.encryption.description') }}</p>
                    </div>
                    <div class="security-card">
                        <div class="security-icon">🛡️</div>
                        <h3>{{ dt('clientsHome.security.privacy.title') }}</h3>
                        <p>{{ dt('clientsHome.security.privacy.description') }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Features for Clients -->
        <section class="client-features">
            <div class="container">
                <h2 class="section-title">{{ dt('clientsHome.featuresTitle') }}</h2>

                <!-- Feature 1: Secure Case Management -->
                <div class="feature-showcase">
                    <div class="feature-content">
                        <h3 class="feature-title">{{ dt('clientsHome.features.caseManagement.title') }}</h3>
                        <ul class="feature-list">
                            <li>✓ {{ dt('clientsHome.features.caseManagement.item1') }}</li>
                            <li>✓ {{ dt('clientsHome.features.caseManagement.item2') }}</li>
                            <li>✓ {{ dt('clientsHome.features.caseManagement.item3') }}</li>
                            <li>✓ {{ dt('clientsHome.features.caseManagement.item4') }}</li>
                        </ul>
                    </div>
                    <div class="feature-image">
                        <img :src="encryptionImage" alt="Secure Case Management" />
                    </div>
                </div>

                <!-- Feature 2: Document Storage -->
                <div class="feature-showcase reverse">
                    <div class="feature-content">
                        <h3 class="feature-title">{{ dt('clientsHome.features.documents.title') }}</h3>
                        <ul class="feature-list">
                            <li>✓ {{ dt('clientsHome.features.documents.item1') }}</li>
                            <li>✓ {{ dt('clientsHome.features.documents.item2') }}</li>
                            <li>✓ {{ dt('clientsHome.features.documents.item3') }}</li>
                            <li>✓ {{ dt('clientsHome.features.documents.item4') }}</li>
                        </ul>
                    </div>
                    <div class="feature-image">
                        <img :src="documentImage" alt="Secure Document Storage" />
                    </div>
                </div>

                <!-- Feature 3: Lawyer Communication -->
                <div class="feature-showcase">
                    <div class="feature-content">
                        <h3 class="feature-title">{{ dt('clientsHome.features.communication.title') }}</h3>
                        <ul class="feature-list">
                            <li>✓ {{ dt('clientsHome.features.communication.item1') }}</li>
                            <li>✓ {{ dt('clientsHome.features.communication.item2') }}</li>
                            <li>✓ {{ dt('clientsHome.features.communication.item3') }}</li>
                            <li>✓ {{ dt('clientsHome.features.communication.item4') }}</li>
                        </ul>
                    </div>
                    <div class="feature-image">
                        <div class="coming-soon-badge">
                            <NBadge value="Coming Soon" type="warning" />
                        </div>
                        <img :src="sharingImage" alt="Lawyer Communication" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Why Choose Our Platform -->
        <section class="why-choose">
            <div class="container">
                <h2 class="section-title">{{ dt('clientsHome.whyChooseTitle') }}</h2>
                <div class="benefits-grid">
                    <div class="benefit-card">
                        <div class="benefit-icon">⚖️</div>
                        <h3>{{ dt('clientsHome.benefits.legalHelp.title') }}</h3>
                        <p>{{ dt('clientsHome.benefits.legalHelp.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">🗣️</div>
                        <h3>{{ dt('clientsHome.benefits.multilingual.title') }}</h3>
                        <p>{{ dt('clientsHome.benefits.multilingual.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">📱</div>
                        <h3>{{ dt('clientsHome.benefits.mobile.title') }}</h3>
                        <p>{{ dt('clientsHome.benefits.mobile.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">🔗</div>
                        <h3>{{ dt('clientsHome.benefits.access.title') }}</h3>
                        <p>{{ dt('clientsHome.benefits.access.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">💼</div>
                        <h3>{{ dt('clientsHome.benefits.organization.title') }}</h3>
                        <p>{{ dt('clientsHome.benefits.organization.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">⚡</div>
                        <h3>{{ dt('clientsHome.benefits.support.title') }}</h3>
                        <p>{{ dt('clientsHome.benefits.support.description') }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- How It Works for Clients -->
        <section class="how-it-works-clients">
            <div class="container">
                <h2 class="section-title">{{ dt('clientsHome.howItWorksTitle') }}</h2>
                <div class="steps-grid">
                    <div class="step-card">
                        <div class="step-number">1</div>
                        <h4>{{ dt('clientsHome.steps.register.title') }}</h4>
                        <p>{{ dt('clientsHome.steps.register.description') }}</p>
                    </div>
                    <div class="step-card">
                        <div class="step-number">2</div>
                        <h4>{{ dt('clientsHome.steps.search.title') }}</h4>
                        <p>{{ dt('clientsHome.steps.search.description') }}</p>
                    </div>
                    <div class="step-card">
                        <div class="step-number">3</div>
                        <h4>{{ dt('clientsHome.steps.connect.title') }}</h4>
                        <p>{{ dt('clientsHome.steps.connect.description') }}</p>
                    </div>
                    <div class="step-card">
                        <div class="step-number">4</div>
                        <h4>{{ dt('clientsHome.steps.manage.title') }}</h4>
                        <p>{{ dt('clientsHome.steps.manage.description') }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Language Coverage -->
        <section class="languages">
            <div class="container">
                <h2 class="section-title">{{ dt('clientsHome.languagesTitle') }}</h2>
                <p class="languages-subtitle">{{ dt('clientsHome.languagesSubtitle') }}</p>
                <div class="language-tags">
                    <NTag v-for="lang in LANGUAGES" :key="lang.value" type="success" size="large" class="lang-tag">
                        {{ lang.label }}
                    </NTag>
                </div>
            </div>
        </section>

        <!-- Final CTA -->
        <section class="final-cta">
            <div class="container">
                <div class="cta-content">
                    <h2>{{ dt('clientsHome.finalCta.title') }}</h2>
                    <p>{{ dt('clientsHome.finalCta.subtitle') }}</p>
                    <div class="cta-buttons">
                        <NButton type="primary" size="large" class="cta-button-large" @click="handleClientRegister">
                            {{ dt('clientsHome.finalCta.button') }}
                        </NButton>
                    </div>
                    <p class="cta-note">{{ dt('clientsHome.finalCta.note') }}</p>
                </div>
            </div>
        </section>

        <!-- For Lawyers Link -->
        <section class="for-lawyers-link">
            <div class="container">
                <p>{{ dt('clientsHome.lookingToJoin') }}
                    <router-link to="/lawyers" class="lawyer-link">{{ dt('clientsHome.goToLawyerSite') }}</router-link>
                </p>
            </div>
        </section>
    </div>
</template>

<style scoped>
.client-page {
    min-height: 100vh;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

/* Client-themed Hero */
.client-hero {
    background: linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%);
    color: white;
    padding: 5rem 0;
}

.hero-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    align-items: center;
}

.hero-content {
    text-align: left;
}

.hero-title {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
}

.hero-subtitle {
    font-size: 1.4rem;
    margin-bottom: 2.5rem;
    opacity: 0.95;
    line-height: 1.6;
}

.cta-buttons {
    display: flex;
    gap: 1rem;
    margin-bottom: 3rem;
}

.cta-button-primary {
    background: #e67e22 !important;
    border: none;
    padding: 0 2.5rem;
    font-size: 1.1rem;
}

.cta-button-secondary {
    background: transparent;
    border: 2px solid white;
    color: white;
    padding: 0 2.5rem;
    font-size: 1.1rem;
}

.cta-button-secondary:hover {
    background: rgba(255, 255, 255, 0.1);
}

.hero-stats {
    display: flex;
    gap: 3rem;
}

.stat-item {
    text-align: center;
}

.stat-number {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
}

.stat-label {
    font-size: 0.9rem;
    opacity: 0.9;
}

.dashboard-preview {
    max-width: 100%;
    border-radius: 15px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

/* Security First Section */
.security-first {
    padding: 5rem 0;
    background: #f8f9fa;
}

.section-title {
    text-align: center;
    font-size: 2.8rem;
    margin-bottom: 1rem;
    color: #8e44ad;
}

.security-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
}

.security-card {
    background: white;
    padding: 2.5rem;
    border-radius: 12px;
    text-align: center;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    border-top: 4px solid #8e44ad;
}

.security-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 30px rgba(142, 68, 173, 0.2);
}

.security-icon {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
}

.security-card h3 {
    color: #2c3e50;
    margin-bottom: 1rem;
    font-size: 1.4rem;
}

.security-card p {
    color: #7f8c8d;
    line-height: 1.6;
}

/* Client Features */
.client-features {
    padding: 5rem 0;
}

.feature-showcase {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    margin: 4rem 0;
}

.feature-showcase.reverse {
    direction: rtl;
}

.feature-showcase.reverse .feature-content {
    direction: ltr;
}

.feature-title {
    font-size: 2rem;
    color: #8e44ad;
    margin-bottom: 1.5rem;
}

.feature-list {
    list-style: none;
    padding: 0;
}

.feature-list li {
    padding: 0.8rem 0;
    font-size: 1.1rem;
    color: #4a5568;
    border-bottom: 1px solid #e2e8f0;
}

.feature-image {
    position: relative;
}

.feature-image img {
    max-width: 100%;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.coming-soon-badge {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
}

/* Why Choose Section */
.why-choose {
    padding: 5rem 0;
    background: #f8f9fa;
}

.benefits-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
}

.benefit-card {
    background: white;
    padding: 2.5rem;
    border-radius: 12px;
    text-align: center;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    border-top: 4px solid #8e44ad;
}

.benefit-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 30px rgba(142, 68, 173, 0.2);
}

.benefit-icon {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
}

.benefit-card h3 {
    color: #2c3e50;
    margin-bottom: 1rem;
    font-size: 1.4rem;
}

.benefit-card p {
    color: #7f8c8d;
    line-height: 1.6;
}

/* How It Works for Clients */
.how-it-works-clients {
    padding: 5rem 0;
    background: linear-gradient(135deg, #f6f9fc 0%, #edf2f7 100%);
}

.steps-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
}

.step-card {
    background: white;
    text-align: center;
    padding: 2.5rem;
    border-radius: 12px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.step-number {
    width: 50px;
    height: 50px;
    background: #8e44ad;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    font-size: 1.5rem;
    font-weight: bold;
}

.step-card h4 {
    color: #2c3e50;
    margin-bottom: 1rem;
    font-size: 1.3rem;
}

.step-card p {
    color: #7f8c8d;
    line-height: 1.6;
}

/* Languages Section */
.languages {
    padding: 5rem 0;
    background: #f8f9fa;
}

.languages-subtitle {
    text-align: center;
    font-size: 1.2rem;
    color: #7f8c8d;
    margin-bottom: 2rem;
}

.language-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
    max-width: 800px;
    margin: 0 auto;
}

.lang-tag {
    font-size: 1.1rem;
}

/* Final CTA */
.final-cta {
    background: linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%);
    padding: 80px 20px;
    text-align: center;
}

.final-cta .container {
    max-width: 1200px;
    margin: 0 auto;
}

.final-cta .cta-content {
    max-width: 800px;
    margin: 0 auto;
}

.final-cta h2 {
    font-size: 2.5rem;
    font-weight: 700;
    color: white;
    margin-bottom: 1rem;
    text-align: center;
}

.final-cta p {
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 2rem;
    text-align: center;
}

.cta-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 2rem 0;
}

.cta-button-large {
    padding: 16px 48px !important;
    font-size: 18px !important;
    font-weight: 600 !important;
    background-color: #e67e22 !important;
    border: none !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    transition: all 0.3s ease !important;
    cursor: pointer;
}

.cta-button-large:hover {
    background-color: #d35400 !important;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
}

.cta-note {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
    margin-top: 1rem;
    text-align: center;
}

/* For Lawyers Link */
.for-lawyers-link {
    padding: 2rem 0;
    text-align: center;
    background: #663788;
    color: white;
}

.lawyer-link {
    color: #e67e22;
    text-decoration: underline;
    font-weight: 600;
}

.lawyer-link:hover {
    color: #d35400;
}

/* Responsive */
@media (max-width: 768px) {
    .final-cta {
        padding: 60px 20px;
    }

    .final-cta h2 {
        font-size: 2rem;
    }

    .final-cta p {
        font-size: 1.1rem;
    }

    .cta-button-large {
        padding: 14px 32px !important;
        font-size: 16px !important;
        width: 100%;
        max-width: 300px;
    }

    .hero-grid,
    .feature-showcase,
    .feature-showcase.reverse {
        grid-template-columns: 1fr;
    }

    .hero-title {
        font-size: 2.2rem;
    }

    .cta-buttons {
        flex-direction: column;
    }

    .hero-stats {
        flex-direction: column;
        gap: 1.5rem;
    }

    .benefits-grid,
    .security-grid {
        grid-template-columns: 1fr;
    }
}
</style>